import { motion } from 'framer-motion'
import { Shield } from 'lucide-react'

const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
}

const crimeTypes = [
    { icon: Shield, label: 'Violência doméstica' },
    { icon: Shield, label: 'Violência sexual' },
    { icon: Shield, label: 'Injúrias e ameaças' },
    { icon: Shield, label: 'Coação e difamação' },
    { icon: Shield, label: 'Discurso de ódio online' },
    { icon: Shield, label: 'Terapias de conversão' },
]

export default function ViolenciaContraPessoasLGBTI() {
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="min-h-screen bg-white"
        >
            <title>Violência contra Pessoas LGBTI+ e Crimes de Ódio | APAV</title>
            <meta name="description" content="Informação sobre as formas de violência que afetam pessoas LGBTI+, do discurso de ódio online às terapias de conversão, e quando constituem crime de ódio." />
            <link rel="canonical" href="https://lgbti.apav.pt/ViolenciaContraPessoasLGBTI" />
            {/* Hero Section */}
            <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-brand-900 to-purple-900 py-20 lg:py-28">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 left-0 w-96 h-96 bg-brand-400 rounded-full filter blur-3xl -translate-x-1/2 -translate-y-1/2" />
                    <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-400 rounded-full filter blur-3xl translate-x-1/2 translate-y-1/2" />
                </div>
                <div className="container-custom relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                    >
                        <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                            Violência contra Pessoas LGBTI+
                        </h1>
                    </motion.div>
                </div>
            </section>

            {/* Intro Text */}
            <section className="pt-10 pb-4 lg:pt-12 lg:pb-4 bg-white">
                <div className="container-custom">
                    <motion.div
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <p className="text-slate-700 leading-relaxed text-xl mb-6 border-l-4 border-brand-500 pl-6">
                            A violência contra pessoas LGBTI+ é toda a forma de violência dirigida a pessoas LGBTI+, independentemente de ter sido praticada por motivo discriminatório ou não.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Crime Types Grid */}
            <section className="pt-4 pb-4 lg:pb-6 bg-slate-50">
                <div className="container-custom">
                    <motion.div
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="mb-10"
                    >
                        <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-3">
                            Formas de crime mais frequentes
                        </h2>
                        <p className="text-slate-600 text-lg">
                            De entre as várias formas de crime que podem afetar as pessoas LGBTI+, podemos destacar as seguintes como mais frequentes:
                        </p>
                    </motion.div>
                    <div>
                        <div className="grid sm:grid-cols-2 gap-x-8">
                            {crimeTypes.map((crime, i) => {
                                const Icon = crime.icon
                                return (
                                    <motion.div
                                        key={i}
                                        variants={fadeUp}
                                        initial="hidden"
                                        whileInView="visible"
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.4, delay: i * 0.07 }}
                                        className="flex items-center gap-4 py-4 border-b border-slate-200 group"
                                    >
                                        <div className="shrink-0 w-9 h-9 rounded-lg bg-rose-100 flex items-center justify-center group-hover:bg-gradient-to-br group-hover:from-rose-500 group-hover:to-pink-600 transition-all duration-300">
                                            <Icon size={18} className="text-rose-600 group-hover:text-white transition-colors duration-300" />
                                        </div>
                                        <span className="font-semibold text-slate-800 text-base group-hover:text-rose-700 transition-colors duration-200">{crime.label}</span>
                                    </motion.div>
                                )
                            })}
                        </div>
                    </div>
                    <motion.div
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="mt-8"
                    >
                        <p className="text-slate-700 leading-relaxed text-lg">
                            Várias formas de discriminação não são consideradas crime, mas são igualmente proibidas pela legislação portuguesa.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Texto unificado */}
            <section className="pt-4 pb-10 lg:pt-4 lg:pb-12 bg-white">
                <div className="container-custom">
                    <motion.div
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <p className="text-slate-700 leading-relaxed text-lg">
                            Quando cometida por motivo discriminatório, a violência contra pessoas LGBTI+ deve ser enquadrada nos <strong>crimes de ódio</strong>, merecendo uma atenção específica dada a dificuldade de enquadramento destes crimes específicos. É muito frequente que a violência dirigida a pessoas LGBTI+ aconteça na internet, sob várias formas, nomeadamente através de <strong>discurso de ódio</strong> e de <strong>incitamento ao ódio e à violência</strong>. A violência praticada contra pessoas LGBTI+ que não aparente basear-se em motivos discriminatórios também merece especial atenção, tendo em conta a <strong>vulnerabilidade particular desta população</strong>. As <strong>terapias de conversão sexual</strong> são também uma forma de violência proibida e criminalizada pela legislação portuguesa.
                        </p>
                    </motion.div>
                </div>
            </section>
        </motion.div>
    )
}
